import React from "react";
import { Quote } from "lucide-react";
import type { Citation } from "../types";
import { formatCitation } from "../utils";
import ArtifactErrorBoundary from "./ArtifactErrorBoundary";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

interface CitationButtonProps {
    citations?: Citation[];
}

// The chips sit inside the clickable message bubble. React events from the portaled popover
// bubble through the React tree, so the chip and the popover both stop the click, or it would
// also select the bubble and toggle the debug drawer.
const stopClick = (e: React.MouseEvent) => e.stopPropagation();

// One chip per source (icon + "Title, Year"). A click or tap on a chip opens that source's
// full text — the excerpt plus the complete "Title, Year, p. N" line. Click, not hover, so it
// works on touch.
const CitationButtonInner: React.FC<CitationButtonProps> = ({ citations }) => {
    if (!citations?.length) return null;

    return (
        <div className="chat-v2-citation-chips">
            {citations.map((c, i) => (
                // Index in the key: the contract doesn't guarantee `id` is unique within one
                // answer's citation list (e.g. two excerpts from the same source).
                <Popover key={`${c.id}-${i}`}>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            className="chat-v2-source-toggle"
                            onClick={stopClick}
                            aria-label={`Source: ${formatCitation(c)}`}
                        >
                            <Quote size={10} aria-hidden="true" />
                            <span className="chat-v2-source-toggle-label">{c.title}, {c.year}</span>
                        </button>
                    </PopoverTrigger>
                    <PopoverContent side="top" className="chat-v2-citation-popover" onClick={stopClick}>
                        <div className="chat-v2-citation-entry">
                            <p className="chat-v2-citation-statement">{c.statement}</p>
                            <p className="chat-v2-citation-source">{formatCitation(c)}</p>
                        </div>
                    </PopoverContent>
                </Popover>
            ))}
        </div>
    );
};

// Citations are a supporting feature riding along on the real answer (caption/summary/artifact) —
// a rendering bug here (e.g. a malformed citation from the backend) must never take the answer
// down with it. The boundary has to wrap CitationButtonInner from the outside: an error thrown
// while CitationButtonInner's own body runs isn't visible to a boundary defined further down in
// the same render, only to one that is its actual parent.
const CitationButton: React.FC<CitationButtonProps> = (props) => (
    <ArtifactErrorBoundary label="citations">
        <CitationButtonInner {...props} />
    </ArtifactErrorBoundary>
);

export default React.memo(CitationButton);
